// `InboxPage` — Wave 5c.1.
//
// Hosts the EPIC-04 left rail (conversations vs leads sub-nav, account
// filter, mode tabs, account-status banner, conversation list) PLUS a
// content slot that 5c.2 / 5c.3 will fill with the message thread + context
// panel. Until then we render a placeholder card so the layout is visible
// in QA / Storybook.
//
// Stories shipped:
//   - US-060 — Conversations / Leads sub-nav.
//   - US-061 — Render the 262 px rail.
//   - US-062 — DS row visual states (delegated to DS-D `ConvRow`).
//   - US-063 — Account filter (DS-D `ConvList`).
//   - US-064 — Mode-tab strip (DS-D `ConvList`).
//   - US-065 — Account-status banner (this module's `<AccountStatusBannerSlot>`).
//   - US-066 — Sort by recent activity (`useConvList` sorts pending-first
//              then desc by `lastMessageAt`).
//   - US-067 — Reopen from the Leads tab — coordination contract surfaced
//              via the search-params (`selectedId`).
//   - US-068 — Source pill (DS-D `ConvRow`).
//   - US-069 — Realtime stream — `useUserHubChannel('message.in', ...)`
//              invalidates the list.
//   - US-070 — Default selection on inbox open.

import { useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useRef, type ReactNode } from 'react';

import {
  conversationMessagesQueryKey,
  invalidateConversations,
  useConversation,
  useConversationMessages,
  useLinkedinAccounts,
  useMarkConversationRead,
  useOnboardingState,
  useUserHubChannel,
  useVoice,
  type ConversationDetail,
  type ConversationListItem,
  type LinkedinAccountRow,
  type ThreadMessage as ApiThreadMessage,
} from '@barel/api-client/react';

import { InboxEmptyState as FtuInboxEmptyState } from '@/features/ftu/InboxEmptyState';
import { useT } from '@barel/i18n';
import { Card, Spinner, Tabs, toast } from '@barel/ui';
import { useQueryClient } from '@tanstack/react-query';

import { TrialExpiredOverlay } from '@/features/billing/TrialExpiredOverlay';

import { ContextPanel } from './ContextPanel';
import { InboxConvList, type InboxModeTabValue } from './ConvList';
import { ThreadPanel } from './ThreadPanel';
import type { AiMode, ConversationView, ThreadMessage } from './types';
import { useConvList } from './useConvList';
import { useInboxFilters, type ConvModeTab } from './ConvFilters';
import { useInboxJk } from './useInboxJk';
import { deriveManualReplyDetected } from './lib/manualReply';
import { derivePolicy } from './lib/modePolicy';
import { toContextPanelData } from './lib/toContextPanelData';

type InboxView = 'conversations' | 'leads';

export interface InboxPageProps {
  /** Initial sub-nav tab — defaults to `conversations` (US-060). */
  initialView?: InboxView;
}

export function InboxPage({ initialView = 'conversations' }: InboxPageProps): ReactNode {
  const t = useT('inbox');
  const navigate = useNavigate();
  const accountsQuery = useLinkedinAccounts();
  const accounts = accountsQuery.data?.accounts ?? [];

  const filters = useInboxFilters();
  const list = useConvList({
    accountId: filters.accountId,
    mode: filters.mode as ConvModeTab,
  });

  // Realtime — US-069 — invalidate the list when a new inbound message lands.
  // The list query re-fetches; the most-recent row bubbles to the top.
  // For the currently-selected conversation we also invalidate its messages
  // cache so the thread panel re-renders the new bubble in chronological
  // order (US-073 / US-079 manual-reply reconcile).
  const qc = useQueryClient();
  const selectedIdRef = filters.selectedId;
  useUserHubChannel('message.in', (event) => {
    const payload = (('payload' in event ? event.payload : null) ?? {}) as {
      conversationId?: string;
    };
    const conversationId = payload.conversationId ?? selectedIdRef;
    invalidateConversations(qc);
    if (conversationId) {
      // The detail envelope carries `unread` + `lastMessageAt`, which drive
      // auto-mark-read for an already-open thread. Refresh it alongside the
      // messages; otherwise the list becomes current while detail remains
      // read and the new inbound is never acknowledged.
      void qc.invalidateQueries({ queryKey: ['conversation', conversationId] });
      void qc.invalidateQueries({ queryKey: conversationMessagesQueryKey(conversationId) });
    }
  });
  useUserHubChannel('message.out', () => {
    invalidateConversations(qc);
    if (selectedIdRef) {
      void qc.invalidateQueries({ queryKey: conversationMessagesQueryKey(selectedIdRef) });
    }
  });
  // SPEC-SEND-TRANSPORT §6 — a LinkedIn delivery failure never fired a
  // refetch before this fix, so the bubble stayed stuck on `sending`
  // forever instead of flipping to the `failed` state + reason line.
  useUserHubChannel('message.send-failed', () => {
    invalidateConversations(qc);
    if (selectedIdRef) {
      void qc.invalidateQueries({ queryKey: conversationMessagesQueryKey(selectedIdRef) });
    }
  });
  useUserHubChannel('account.status', () => {
    void qc.invalidateQueries({ queryKey: ['linkedin-accounts'] });
  });

  // Wave A2 — conversation lifecycle. The DO emits these so the inbox
  // updates without a full page refetch:
  //
  //   - `conv.created`: a new conversation row landed → invalidate the list.
  //   - `conv.updated`: classifier or workflow patched a row → invalidate so
  //     `useConvList` re-renders the new temperature / off-target chip.
  //   - `conv.merged`: a runtime alias-collapse merged loser → survivor;
  //     drop the loser id from cache and, if the user was viewing it, hop
  //     selection to the survivor + show a toast.
  useUserHubChannel('conv.created', () => {
    invalidateConversations(qc);
  });
  useUserHubChannel('conv.updated', (event) => {
    const payload = (('payload' in event ? event.payload : null) ?? {}) as {
      conversationId?: string;
    };
    invalidateConversations(qc);
    // Detail uses the singular `conversationKey(id)` shape. Invalidating the
    // old plural key left other-tab mark-read events stale indefinitely.
    if (payload.conversationId) {
      void qc.invalidateQueries({ queryKey: ['conversation', payload.conversationId] });
    } else if (selectedIdRef) {
      void qc.invalidateQueries({ queryKey: ['conversation', selectedIdRef] });
    }
  });
  useUserHubChannel('conv.merged', (event) => {
    const payload = (('payload' in event ? event.payload : null) ?? {}) as {
      loserConversationId?: string;
      survivorConversationId?: string;
    };
    invalidateConversations(qc);
    if (payload.loserConversationId) {
      // Drop the loser's per-conversation caches.
      void qc.invalidateQueries({ queryKey: ['conversations', payload.loserConversationId] });
      void qc.invalidateQueries({
        queryKey: conversationMessagesQueryKey(payload.loserConversationId),
      });
      if (selectedIdRef === payload.loserConversationId && payload.survivorConversationId) {
        filters.setSelectedId(payload.survivorConversationId);
        toast.info({ title: t('conv.merged_toast') });
      }
    }
  });

  // D24 / US-248 — power-user j/k navigation. Window-level so it fires from
  // the rail OR from inside the thread reader. The hook is a no-op when no
  // items are loaded and is internally guarded against editable targets so
  // typing "j" in the reply composer / search bar does nothing.
  useInboxJk({
    items: list.items,
    selectedId: filters.selectedId,
    onSelect: filters.setSelectedId,
  });

  // US-070 — default selection on first mount: most recent unread in
  // non-`auto` mode, else most recent overall.
  useEffect(() => {
    if (filters.selectedId) return;
    const candidates = list.items.filter((c) => c.accountMode !== 'auto');
    const fallback = candidates.find((c) => c.unread) ?? candidates[0] ?? list.items[0] ?? null;
    if (fallback) {
      filters.setSelectedId(fallback.id);
    }
    // We deliberately depend on `list.items` so the default kicks in once
    // the first page loads. Re-running it after a manual deselect is a
    // no-op because the user's `null` selection was an explicit gesture
    // (handled by `onSelect`).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list.items.length]);

  const counts = useMemo(() => {
    const out: Record<InboxModeTabValue, number> = {
      all: list.items.length,
      action: 0,
      auto: 0,
      review: 0,
      offtarget: 0,
    };
    for (const conv of list.items) {
      if (conv.pendingActionType) out.action += 1;
      if (conv.accountMode === 'auto') out.auto += 1;
      if (conv.accountMode === 'review') out.review += 1;
      if (conv.offTarget) out.offtarget += 1;
    }
    return out;
  }, [list.items]);

  const showOffTargetTab = accounts.some((a) => a.showOffTarget === true);

  const onNavigateToAccounts = () => {
    void navigate({ to: '/accounts' });
  };

  // ─── Sub-nav ─────────────────────────────────────────────────────────────
  // Leads tab routing is a coordination point with 5d.2 (Leads epic). We
  // navigate via a string literal so the cast survives even after `/leads`
  // is registered in the route tree.
  const subnav = (
    <Tabs
      value={initialView}
      onValueChange={(v) => {
        if (v === 'leads') {
          void navigate({ to: '/leads' as never });
        }
      }}
      variant="underline"
      size="md"
      data-testid="inbox-subnav"
    >
      <Tabs.List aria-label={t('subnav.ariaLabel')}>
        <Tabs.Trigger value="conversations">{t('subnav.conversations')}</Tabs.Trigger>
        <Tabs.Trigger value="leads">{t('subnav.leads')}</Tabs.Trigger>
      </Tabs.List>
      <Tabs.Content value="conversations" hidden forceMount style={{ display: 'none' }} />
      <Tabs.Content value="leads" hidden forceMount style={{ display: 'none' }} />
    </Tabs>
  );

  // ─── Content ─────────────────────────────────────────────────────────────
  const selected = filters.selectedId ? list.items.find((c) => c.id === filters.selectedId) : null;
  // US-037 — surface a "we're listening" cue during the FTU window when the
  // user has zero conversations to look at. Active iff onboarding is complete
  // AND the list isn't loading + has no items.
  const onboardingState = useOnboardingState();
  const ftuActive = onboardingState.data?.completedAt != null;
  const showFtuEmptyState =
    ftuActive && !list.isLoading && !list.isError && list.items.length === 0;

  return (
    <section
      data-route="_authed/inbox"
      data-testid="inbox-page"
      className="flex flex-col gap-3"
      style={{ minHeight: 'calc(100vh - var(--space-6) * 2)' }}
    >
      {subnav}
      {/* US-200 — soft-block banner (read-only inbox while trial expired). */}
      <TrialExpiredOverlay inline>
        <></>
      </TrialExpiredOverlay>
      <div
        style={{
          display: 'flex',
          gap: 'var(--space-4)',
          flex: '1 1 auto',
          minHeight: 0,
        }}
      >
        <ConvListColumn
          accounts={accounts}
          accountsLoading={accountsQuery.isLoading}
          list={list}
          filters={filters}
          counts={counts}
          showOffTargetTab={showOffTargetTab}
          onNavigateToAccounts={onNavigateToAccounts}
        />
        {showFtuEmptyState ? (
          <div style={{ flex: '1 1 auto' }}>
            <FtuInboxEmptyState />
          </div>
        ) : (
          <ConvDetailColumn
            selectedId={filters.selectedId}
            selectedItem={selected ?? null}
            accounts={accounts}
          />
        )}
      </div>
    </section>
  );
}

interface ConvListColumnProps {
  accounts: LinkedinAccountRow[];
  accountsLoading: boolean;
  list: ReturnType<typeof useConvList>;
  filters: ReturnType<typeof useInboxFilters>;
  counts: Record<InboxModeTabValue, number>;
  showOffTargetTab: boolean;
  onNavigateToAccounts: () => void;
}

function ConvListColumn({
  accounts,
  accountsLoading,
  list,
  filters,
  counts,
  showOffTargetTab,
  onNavigateToAccounts,
}: ConvListColumnProps): ReactNode {
  const t = useT('inbox');
  if (list.isLoading || accountsLoading) {
    return (
      <Card
        padding="lg"
        radius="2xl"
        data-testid="inbox-loading"
        style={{ width: 262, alignSelf: 'flex-start' }}
      >
        <div className="flex flex-col items-center gap-2">
          <Spinner aria-label={t('list.loadingLabel')} />
          <span className="text-sm text-secondary">{t('list.loadingLabel')}</span>
        </div>
      </Card>
    );
  }
  if (list.isError) {
    return (
      <Card
        padding="lg"
        radius="2xl"
        data-testid="inbox-error"
        style={{ width: 262, alignSelf: 'flex-start' }}
      >
        <div className="flex flex-col gap-2">
          <strong>{t('list.errorTitle')}</strong>
          <button
            type="button"
            onClick={list.refetch}
            className="text-sm text-accent"
            data-testid="inbox-error-retry"
          >
            {t('list.errorRetry')}
          </button>
        </div>
      </Card>
    );
  }
  return (
    <div style={{ flexShrink: 0 }} data-testid="inbox-conv-list">
      <InboxConvList
        accounts={accounts}
        conversations={list.items}
        selectedAccountId={filters.accountId as string | 'all'}
        onSelectAccount={(next) => filters.setAccountId(next as string)}
        modeTab={filters.mode as InboxModeTabValue}
        onModeTabChange={(next) => filters.setMode(next)}
        modeTabCounts={counts}
        showOffTargetTab={showOffTargetTab}
        selectedId={filters.selectedId}
        onSelect={filters.setSelectedId}
        onNavigateToAccounts={onNavigateToAccounts}
      />
      {list.hasNextPage ? (
        <button
          type="button"
          onClick={list.fetchNextPage}
          disabled={list.isFetchingNextPage}
          className="mt-2 w-full text-sm text-secondary"
          data-testid="inbox-load-more"
        >
          {list.isFetchingNextPage ? t('list.loadingLabel') : t('list.loadMore')}
        </button>
      ) : null}
    </div>
  );
}

interface ConvDetailColumnProps {
  selectedId: string | null;
  selectedItem: ConversationListItem | null;
  accounts: LinkedinAccountRow[];
}

function ConvDetailColumn({
  selectedId,
  selectedItem,
  accounts,
}: ConvDetailColumnProps): ReactNode {
  const t = useT('inbox');
  const navigate = useNavigate();

  const conversationQuery = useConversation(selectedId ?? undefined);
  const messagesQuery = useConversationMessages(selectedId ?? undefined);
  const { mutate: markConversationRead } = useMarkConversationRead();
  const attemptedReadVersions = useRef(new Set<string>());

  // The detail response is authoritative; the list row provides the
  // optimistic bridge while it loads. Include lastMessageAt in the attempt
  // key so a new inbound on an already-open thread is marked read once too,
  // while a rejected billing-guarded write does not retry on every render.
  const envelope = conversationQuery.data?.conversation ?? selectedItem ?? null;
  const unreadVersion =
    selectedId && envelope?.unread
      ? `${selectedId}:${envelope.lastMessageAt ?? 'unknown-activity'}`
      : null;
  useEffect(() => {
    if (!selectedId || !unreadVersion || attemptedReadVersions.current.has(unreadVersion)) return;
    attemptedReadVersions.current.add(unreadVersion);
    markConversationRead({ conversationId: selectedId });
  }, [markConversationRead, selectedId, unreadVersion]);

  // EPIC-07 (US-088) — the hot-override policy needs `voice.notifyHotAction`,
  // which only lives on the per-account voice profile. Cached per account by
  // react-query; disabled until we know which account owns the selected conv.
  const voiceAccountId =
    conversationQuery.data?.conversation?.accountId ?? selectedItem?.accountId ?? undefined;
  const voiceQuery = useVoice(voiceAccountId);

  if (!selectedId) {
    return (
      <Card padding="lg" radius="2xl" data-testid="inbox-empty-detail" style={{ flex: '1 1 auto' }}>
        <div className="flex flex-col items-center gap-2 py-6 text-center">
          <strong>{t('list.emptyTitle')}</strong>
          <span className="text-sm text-secondary">{t('list.emptySubtitle')}</span>
        </div>
      </Card>
    );
  }

  // The single-envelope `useConversation` is the source of truth; while it
  // loads we fall back to the row from the list (covers the optimistic
  // moment between row click and detail fetch). Messages use the dedicated
  // cursor-paginated query.
  const isLoading = (conversationQuery.isLoading && !selectedItem) || messagesQuery.isLoading;

  if (isLoading || !envelope) {
    return (
      <Card
        padding="lg"
        radius="2xl"
        data-testid="inbox-thread-loading"
        style={{ flex: '1 1 auto' }}
      >
        <div className="flex flex-col items-center gap-2 py-6">
          <Spinner aria-label={t('list.loadingLabel')} />
          <span className="text-sm text-secondary">{t('list.loadingLabel')}</span>
        </div>
      </Card>
    );
  }

  const conversation = toConversationView(envelope);
  const apiMessages = messagesQuery.data?.pages.flatMap((p) => p.messages ?? []) ?? [];
  const messages = apiMessages.map(toFeatureMessage);
  const aiMode: AiMode = conversation.account.mode === 'auto' ? 'auto' : 'review';
  const manualReplyDetected = deriveManualReplyDetected(messages);
  const offTargetBlocked = conversation.offTarget && conversation.offTargetMode === 'block';

  // Wave B2 — wire the right-rail ContextPanel with real envelope fields.
  // The detail-shape envelope only lands once `useConversation` resolves;
  // the list-row fallback doesn't carry the extra fields, so we feed the
  // adapter the detail envelope only — it returns `null` mid-load and the
  // wrapper (`if (conv === null) return null`) keeps the rail empty.
  const detailEnvelope = (conversationQuery.data?.conversation ??
    null) as ConversationDetail | null;
  const accountForPanel = accounts.find((a) => a.id === conversation.account.id) ?? null;
  const contextPanelData = toContextPanelData({
    envelope: detailEnvelope,
    account: accountForPanel,
    contact: {
      title: conversation.contactRole,
      company: conversation.contactCompany,
    },
  });

  // EPIC-07 — derive the AI policy banners (US-087 / US-088) from account +
  // voice + conversation state. `autoDetected`: the server-side auto-capture
  // path (extIngest, D23) flips `leadPushed` without recording an outcome,
  // while every manual push writes `outcomeStatus = 'client'` — so "pushed
  // with no outcome" is the persisted marker for an AI auto-push.
  const policy = derivePolicy({
    account: {
      aiMode,
      offTargetMode: conversation.offTargetMode ?? accountForPanel?.offTargetMode ?? 'block',
      autoLeadDetect: accountForPanel?.autoLeadDetect ?? false,
    },
    voice: { notifyHotAction: voiceQuery.data?.voice.notifyHotAction ?? 'auto' },
    conversation: {
      isVeryHot: conversation.isVeryHot ?? false,
      offTarget: conversation.offTarget ?? false,
      autoDetected: Boolean(conversation.leadPushed && conversation.outcomeStatus == null),
      channelShareLocked: conversation.channelShareLocked ?? false,
    },
  });

  return (
    <ThreadPanel
      conversation={conversation}
      messages={messages}
      aiMode={aiMode}
      hotOverride={conversation.isVeryHot ?? false}
      showHotOverride={policy.flags.showHotOverride}
      autoLeadDetected={policy.flags.showAutoLeadIndicator}
      offTargetBlocked={offTargetBlocked}
      manualReplyDetected={manualReplyDetected}
      contextPanel={
        <ContextPanel
          conv={contextPanelData}
          onNavSettings={() => {
            void navigate({
              to: '/voice/$linkedinAccountId',
              params: { linkedinAccountId: conversation.account.id },
            } as never);
          }}
        />
      }
      onNavSettings={() => {
        void navigate({
          to: '/voice/$linkedinAccountId',
          params: { linkedinAccountId: conversation.account.id },
        } as never);
      }}
      onViewLeads={() => {
        void navigate({ to: '/leads' as never });
      }}
      onUpgrade={() => {
        void navigate({ to: '/settings/billing' as never });
      }}
    />
  );
}

/**
 * Adapt the API `ConversationListItem` envelope into the local
 * `ConversationView` shape that `ThreadPanel` consumes. Mirrors
 * `toDsConversation` in `ConvList.tsx` but for the thread surface.
 *
 * Contact display/profile metadata is already sanitized by the API. Raw
 * LinkedIn member/profile URNs deliberately never enter this view model.
 */
function toConversationView(envelope: ConversationListItem): ConversationView {
  // The list-side envelope has these defaulted to false/null when the
  // server-side `loadRelancesEnabledByAccount` hasn't run for this row yet.
  // Use nullish-aware reads so older cached payloads still type-check.
  const isVeryHot = (envelope as { isVeryHot?: boolean }).isVeryHot ?? false;
  const offTargetMode = (envelope as { offTargetMode?: ConversationView['offTargetMode'] })
    .offTargetMode;
  const relancesEnabled = (envelope as { relancesEnabled?: boolean }).relancesEnabled ?? true;
  const outcomeStatus =
    (envelope as { outcomeStatus?: ConversationView['outcomeStatus'] }).outcomeStatus ?? null;
  const relanceScheduledAt =
    (envelope as { relanceScheduledAt?: number | null }).relanceScheduledAt ?? null;
  const channelShareLocked =
    (envelope as { channelShareLocked?: boolean }).channelShareLocked ?? false;
  // Wave B2 — single-conv detail envelope carries `businesses` (top-3 by
  // affinity score). The list envelope doesn't, so we fall back to the
  // primary business name when present (still produces a chip on the row).
  const detailBusinesses = (
    envelope as { businesses?: { id: string; name: string; score: number }[] }
  ).businesses;
  const primaryBusinessName = (envelope as { primaryBusinessName?: string | null })
    .primaryBusinessName;
  const businesses: ConversationView['businesses'] = detailBusinesses
    ? detailBusinesses.map((b) => ({ id: b.id, name: b.name, score: b.score }))
    : primaryBusinessName
      ? [primaryBusinessName]
      : [];
  return {
    id: envelope.id,
    contactName: envelope.contactName,
    contactInitials: envelope.contactInitials,
    contactRole: envelope.contactRole,
    contactCompany: envelope.contactCompany,
    contactVanitySlug: envelope.contactVanitySlug,
    contactAvatarUrl: envelope.contactAvatarUrl,
    contactProfileUrl: envelope.contactProfileUrl,
    account: {
      id: envelope.accountId,
      name: envelope.accountName,
      initials: envelope.accountInitials,
      color: envelope.accountColor ?? undefined,
      mode: envelope.accountMode,
      status: envelope.accountStatus,
    },
    businesses,
    temperature: envelope.temperature ?? undefined,
    offTarget: envelope.offTarget,
    offTargetReason: envelope.offTargetReason,
    offTargetMode: offTargetMode ?? undefined,
    pendingAction: envelope.pendingActionType
      ? {
          type: envelope.pendingActionType,
          label: envelope.pendingActionLabel ?? '',
          icon: envelope.pendingActionIcon ?? undefined,
          detail: envelope.pendingActionDetail ?? undefined,
          reason: envelope.pendingActionReason ?? undefined,
          autoCompleted: envelope.pendingActionAutoCompleted,
        }
      : undefined,
    leadPushed: envelope.leadPushed,
    isVeryHot,
    relancesEnabled,
    outcomeStatus,
    relanceScheduledAt,
    channelShareLocked,
  };
}

function toIso(value: number | null): string | null {
  if (value == null) return null;
  return new Date(value).toISOString();
}

/** Map the api-client `ThreadMessage` (epoch numbers) to the local
 *  `ThreadMessage` shape (ISO strings) that `MessageBubbles` expects. */
function toFeatureMessage(m: ApiThreadMessage): ThreadMessage {
  return {
    id: m.id,
    conversationId: m.conversationId,
    role: m.role,
    body: m.body,
    systemDetail: m.systemDetail,
    outboundSource: m.outboundSource,
    sentAt: toIso(m.sentAt),
    receivedAt: toIso(m.receivedAt),
    createdAt: toIso(m.createdAt) ?? new Date(0).toISOString(),
    deliveryState: m.deliveryState,
    deliveryError: m.deliveryError,
  };
}
